import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';
import { createPopper } from '@popperjs/core';

import { cn } from '@/lib/utils';
import { COMMON } from '@/constants/commonText.constants';
import { useTran } from '@/hooks/useTran';

export function Select({
	className,
	selectClassName,
	label,
	error,
	options = [],
	onChange,
	value,
	placeholder = 'Select an option',
	disabled,
	id,
	labelClassName,
	required,
	customT,
	searchable = true,
}) {
	const t = customT || useTran().t;
	const [isOpen, setIsOpen] = useState(false);
	const [isPositioned, setIsPositioned] = useState(false);
	const [searchQuery, setSearchQuery] = useState('');
	const [focusedIndex, setFocusedIndex] = useState(-1);

	const inputRef = useRef(null);
	const dropdownRef = useRef(null);
	const chevronRef = useRef(null);
	const searchInputRef = useRef(null);
	const popperInstanceRef = useRef(null);

	const selectedOption = options.find((option) => option.key === value);

	const filteredOptions = useMemo(() => {
		if (!searchQuery.trim()) return options;

		const query = searchQuery.toLowerCase().trim();
		const results = [];

		const calculateFuzzyScore = (text, query) => {
			if (query.length === 0) return 0;

			let matches = 0;
			let textIndex = 0;

			for (let i = 0; i < query.length; i++) {
				const char = query[i];
				const foundIndex = text.indexOf(char, textIndex);

				if (foundIndex !== -1) {
					matches++;
					textIndex = foundIndex + 1;
				}
			}

			return matches / query.length;
		};

		const scoreOption = (option) => {
			const text = t(option.value).toLowerCase();
			const searchText = option.searchText?.toLowerCase() || '';

			if (text === query) return 1000;
			if (text.startsWith(query)) return 800;
			if (text.includes(` ${query} `) || text.includes(` ${query}`) || text.includes(`${query} `)) return 600;
			if (text.includes(query)) return 400;
			if (searchText.includes(query)) return 300;

			const fuzzyScore = calculateFuzzyScore(text, query);
			if (fuzzyScore > 0.7) return Math.floor(fuzzyScore * 200);

			return 0;
		};

		for (const option of options) {
			const score = scoreOption(option);
			if (score > 0) {
				results.push({ ...option, _score: score });
			}
		}

		return results.sort((a, b) => b._score - a._score);
	}, [options, searchQuery, t]);

	useEffect(() => {
		setFocusedIndex(-1);
	}, [filteredOptions]);

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (
				!chevronRef.current?.contains(event.target) &&
				!dropdownRef.current?.contains(event.target) &&
				!inputRef.current?.contains(event.target)
			) {
				setIsOpen(false);
				setSearchQuery('');
			}
		};

		const createPopperInstance = () => {
			if (inputRef.current && dropdownRef.current) {
				popperInstanceRef.current = createPopper(inputRef.current, dropdownRef.current, {
					placement: 'bottom-start',
					onFirstUpdate: () => {
						setIsPositioned(true);
					},
					modifiers: [
						{
							name: 'sameWidth',
							enabled: true,
							phase: 'beforeWrite',
							requires: ['computeStyles'],
							fn: ({ state }) => {
								state.styles.popper.width = `${state.rects.reference.width}px`;
							},
						},
						{
							name: 'flip',
							options: {
								fallbackPlacements: ['top-start', 'bottom-start'],
								padding: 8,
							},
						},
						{
							name: 'preventOverflow',
							options: {
								boundary: 'viewport',
								padding: 8,
							},
						},
						{
							name: 'offset',
							options: {
								offset: [0, 4],
							},
						},
					],
					strategy: 'fixed',
				});

				// Avoid a “jump from (0,0)” on first paint.
				popperInstanceRef.current.update();
			}
		};

		if (isOpen) {
			setIsPositioned(false);
			document.addEventListener('mousedown', handleClickOutside);
			createPopperInstance();

			if (searchable) {
				setTimeout(() => searchInputRef.current?.focus(), 0);
			}
		} else if (popperInstanceRef.current) {
			setIsPositioned(false);
			popperInstanceRef.current.destroy();
			popperInstanceRef.current = null;
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
			if (popperInstanceRef.current) {
				popperInstanceRef.current.destroy();
				popperInstanceRef.current = null;
			}
		};
	}, [isOpen, searchable]);

	const handleSelectOption = (option) => {
		if (option.disabled) return;

		const syntheticEvent = {
			target: {
				value: option.key,
				id,
			},
		};
		onChange?.(syntheticEvent);
		setIsOpen(false);
		setSearchQuery('');
		setFocusedIndex(-1);
	};

	const handleKeyDown = (e) => {
		if (!isOpen) {
			if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
				e.preventDefault();
				setIsOpen(true);
			}
			return;
		}

		switch (e.key) {
			case 'ArrowDown':
				e.preventDefault();
				setFocusedIndex((prev) => (prev < filteredOptions.length - 1 ? prev + 1 : 0));
				break;
			case 'ArrowUp':
				e.preventDefault();
				setFocusedIndex((prev) => (prev > 0 ? prev - 1 : filteredOptions.length - 1));
				break;
			case 'Enter':
				e.preventDefault();
				if (focusedIndex >= 0 && filteredOptions[focusedIndex]) {
					handleSelectOption(filteredOptions[focusedIndex]);
				}
				break;
			case 'Escape':
				e.preventDefault();
				setIsOpen(false);
				setSearchQuery('');
				setFocusedIndex(-1);
				break;
		}
	};

	const toggleDropdown = (e) => {
		e.preventDefault();
		if (!disabled) {
			setIsOpen(!isOpen);
			if (!isOpen) {
				setSearchQuery('');
			}
		}
	};

	const highlightMatch = (text, query) => {
		if (!query.trim()) return text;

		const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
		const regex = new RegExp(`(${escapedQuery})`, 'gi');
		const parts = text.split(regex);

		return parts.map((part, index) =>
			regex.test(part) ? (
				<mark
					key={index}
					className="bg-chart-2/30 text-chart-2 px-0.5 rounded"
				>
					{part}
				</mark>
			) : (
				part
			),
		);
	};

	return (
		<div
			className={cn('space-y-1', className)}
			id={id}
		>
			{label && (
				<label className={cn('block font-medium text-foreground', labelClassName)}>
					{t(label)}
					{required && <span className="text-destructive"> *</span>}
				</label>
			)}

			<div className="relative w-full">
				<div
					ref={inputRef}
					className={cn(
						'bg-background relative w-full h-10 border-input border rounded-md focus-within:border-ring focus-within:bg-accent/50',
						error && 'border-destructive',
						disabled && 'cursor-not-allowed opacity-70',
						selectClassName,
					)}
					onClick={() => !disabled && setIsOpen(true)}
					onKeyDown={handleKeyDown}
					tabIndex={disabled ? -1 : 0}
				>
					<div className="flex items-center h-full px-3 cursor-pointer min-w-0 overflow-hidden">
						<span
							className={cn(
								'flex-1 min-w-0 text-left truncate text-foreground block',
								!selectedOption && 'text-muted-foreground',
							)}
						>
							{selectedOption ? t(selectedOption.value) : t(placeholder)}
						</span>

						<button
							ref={chevronRef}
							type="button"
							className={cn(
								'ml-2 p-1 text-muted-foreground hover:text-foreground rounded',
								disabled && 'cursor-not-allowed',
							)}
							onClick={(e) => {
								e.stopPropagation();
								toggleDropdown(e);
							}}
							aria-label="Toggle dropdown"
							disabled={disabled}
						>
							<ChevronDown className={cn('w-4 h-4 transition-transform', isOpen && 'transform rotate-180')} />
						</button>
					</div>
				</div>

				{isOpen && (
					<div
						ref={dropdownRef}
						className="z-[300] bg-card rounded-md shadow-lg ring-1 ring-border focus:outline-none"
						style={{
							opacity: isPositioned ? 1 : 0,
							visibility: isPositioned ? 'visible' : 'hidden',
							transition: isPositioned ? 'opacity 0.2s ease' : 'none',
						}}
					>
						{searchable && (
							<div className="p-2 border-b border-border">
								<div className="relative">
									<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
									<input
										ref={searchInputRef}
										type="text"
										className="w-full pl-10 pr-3 py-2 border border-input bg-background text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
										placeholder={`Search ${t(placeholder).toLowerCase()}...`}
										value={searchQuery}
										onChange={(e) => setSearchQuery(e.target.value)}
										onKeyDown={handleKeyDown}
									/>
								</div>
							</div>
						)}

						<ul
							className="!ml-0 max-h-60 py-1 overflow-auto"
							role="listbox"
							id="options-list"
						>
							{filteredOptions.length === 0 ? (
								<li className="px-3 py-2 text-muted-foreground text-center">
									{searchQuery ? `No results for \"${searchQuery}\"` : t(COMMON.NO_OPTIONS_FOUND)}
								</li>
							) : (
								filteredOptions.map((option, index) => (
									<li
										key={option.key}
										className={cn(
											'px-3 py-2 flex items-center justify-between transition-colors',
											value === option.key
												? 'bg-accent/20 text-primary'
												: index === focusedIndex
													? 'bg-muted'
													: 'hover:bg-muted',
											option.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
										)}
										onClick={() => handleSelectOption(option)}
										role="option"
										aria-selected={value === option.key}
									>
										<span className="flex-1">{highlightMatch(t(option.value), searchQuery)}</span>
										{value === option.key && <Check className="w-4 h-4 text-primary ml-2" />}
									</li>
								))
							)}
						</ul>
					</div>
				)}
			</div>

			{error && <small className="text-destructive text-xs">{error}</small>}
		</div>
	);
}
